import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { FiMail, FiSearch, FiUser, FiFileText, FiFilter, FiHome, FiMessageSquare } from "react-icons/fi";

import api from "@/services/api";
import ActionsMenu from "./actions-menu";

export default function JobHeader({ job, setJob, posts, setShowImportModal }) {
  const { id } = useParams();
  const location = useLocation();
  const [counts, setCounts] = useState({ ad: 0, pool: 0, hunt: 0 });

  useEffect(() => {
    if (!job?._id) return;
    (async () => {
      try {
        const [ad, pool, hunt] = await Promise.all([
          api.post("/application/search", { job_id: job._id, source: "AD", limit: 0 }),
          api.post("/application/search", { job_id: job._id, source: "POOL", limit: 0 }),
          api.post("/application/search", { job_id: job._id, source: "HUNT", limit: 0 }),
        ]);
        setCounts({ ad: ad.total || 0, pool: pool.total || 0, hunt: hunt.total || 0 });
      } catch (e) {
        console.error(e);
      }
    })();
  }, [job?._id]);

  const activeBoards = posts?.filter((p) => p.status === "active").length || 0;
  const status = getStatusBadge(job?.status);

  const onAdConfig = location.pathname.includes(`/${id}/ad`);
  const onAdPath = location.pathname === `/recruiter/job-ads/${id}` || onAdConfig || location.pathname.endsWith("/candidates");

  const cards = [
    {
      key: "ad",
      to: onAdConfig ? `/recruiter/job-ads/${id}` : `/recruiter/job-ads/${id}/ad`,
      label: "Ad",
      icon: FiMail,
      iconCls: "bg-blue-50 text-blue-600",
      count: counts.ad,
      unit: "cand.",
      sub: `${status.label} · ${activeBoards} boards`,
      active: onAdPath,
      disabled: false,
    },
    {
      key: "pool",
      to: `/recruiter/job-ads/${id}/pool`,
      label: "Pool",
      icon: FiSearch,
      iconCls: "bg-green-50 text-green-600",
      count: counts.pool,
      unit: "matches",
      sub: null,
      active: location.pathname.endsWith("/pool"),
      disabled: false,
    },
    {
      key: "hunt",
      to: `/recruiter/job-ads/${id}/hunt`,
      label: "Hunt",
      icon: FiUser,
      iconCls: "bg-amber-50 text-amber-600",
      count: counts.hunt,
      unit: "profiles",
      sub: "Coming soon",
      active: location.pathname.endsWith("/hunt"),
      disabled: false,
    },
  ];

  return (
    <div className="bg-white px-6 pt-3 pb-4 shrink-0 border-b border-gray-200">
      <div className="flex items-start gap-6">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Link to="/recruiter/job-ads" className="text-sm text-gray-400 hover:text-gray-600 shrink-0">Jobs</Link>
            <span className="text-gray-300 shrink-0">›</span>
            <h1 className="text-2xl font-bold text-gray-900 truncate min-w-0">{job?.title || "Untitled"}</h1>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1 shrink-0 ${status.cls}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-current" />
              {status.label}
            </span>
            <div className="ml-auto shrink-0">
              <ActionsMenu job={job} setJob={setJob} posts={posts} setShowImportModal={setShowImportModal} />
            </div>
          </div>
          <div className="flex items-center gap-1">
            {[
              { to: `/recruiter/job-ads/${id}`, label: "Home", icon: FiHome },
              { to: `/recruiter/job-ads/${id}/brief`, label: "Brief", icon: FiFileText },
              { to: `/recruiter/job-ads/${id}/screening`, label: "Screening", icon: FiFilter },
              { to: `/recruiter/job-ads/${id}/interview`, label: "Interview", icon: FiMessageSquare },
            ].map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg hover:bg-gray-100 ${location.pathname === to ? "bg-blue-50 text-blue-600" : "text-gray-500"}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-xs font-medium">{label}</span>
              </Link>
            ))}
          </div>
        </div>

        <div className="border-l border-gray-200 self-stretch" />

        <div className="flex gap-3 shrink-0">
          {cards.map((c) => {
            const Icon = c.icon;
            const baseCls = `flex items-center gap-3 min-w-[180px] px-4 py-3 rounded-xl border transition-all ${
              c.active ? "border-blue-500 bg-blue-50/30 shadow-sm" : "border-gray-200 bg-white"
            }`;
            const inner = (
              <>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.iconCls}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <div className="text-xs text-gray-500">{c.label}</div>
                  <div className="text-sm font-semibold text-gray-900">
                    {c.count} <span className="text-gray-400 font-normal">{c.unit}</span>
                  </div>
                  <div className="text-xs text-gray-400">{c.sub}</div>
                </div>
              </>
            );
            if (c.disabled) {
              return (
                <div key={c.key} className={`${baseCls} opacity-50 cursor-not-allowed`} title={c.sub}>
                  {inner}
                </div>
              );
            }
            return (
              <Link key={c.key} to={c.to} className={`${baseCls} hover:border-gray-300 hover:shadow-sm`}>
                {inner}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function getStatusBadge(status) {
  if (status === "OPEN") return { label: "Open", cls: "bg-emerald-50 text-emerald-700" };
  if (status === "DRAFT") return { label: "Draft", cls: "bg-gray-100 text-gray-600" };
  return { label: "Closed", cls: "bg-gray-100 text-gray-600" };
}
